import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Chip } from "@mui/material";
import {
  BoltRounded,
  LogoutRounded,
  WaterDropOutlined,
  EvStation,
  SolarPowerOutlined,
  ReceiptLongOutlined,
  Inventory2Outlined,
  NotificationsActiveOutlined,
  ArrowForwardRounded,
  SmartToyOutlined,
  ManageSearchOutlined,
  SettingsRemoteOutlined,
  FilterAltOutlined,
  BuildOutlined,
  TranslateOutlined,
  KeyboardArrowDownRounded,
  KeyboardArrowUpRounded,
  AutoAwesomeOutlined,
  KeyboardVoiceRounded,
} from "@mui/icons-material";
import { useAuth } from "../../AuthContext";
import { demo, glassCard } from "./demoStyles";
import ChatWidget from "./ChatWidget";

// ── Systems ─────────────────────────────────────────────────────────────────
const SYSTEMS = [
  { key: "rms",     title: "Remote Monitoring System", desc: "Live pump telemetry, sessions, consumers, complaints and reports across your fleet.", icon: WaterDropOutlined,         status: "live", to: "/dashboard",           gradient: "linear-gradient(135deg, #3b82f6, #22d3ee)" },
  { key: "ev",      title: "EV Chargers",               desc: "Real-time charger availability, active sessions, energy dispensed and revenue analytics.", icon: EvStation,            status: "live", to: "/demo/ev-chargers",     gradient: "linear-gradient(135deg, #34d399, #22d3ee)" },
  { key: "solar",   title: "Solar Analytics",           desc: "Generation, performance ratio and yield insights for every plant.", icon: SolarPowerOutlined,             status: "live", to: "/demo/solar-analytics", gradient: "linear-gradient(135deg, #fbbf24, #f59e0b)" },
  { key: "billing", title: "Billing & Payments",        desc: "Automated invoicing, subsidy reconciliation and collection tracking.", icon: ReceiptLongOutlined,            status: "live", to: "/demo/billing",          gradient: "linear-gradient(135deg, #a78bfa, #6366f1)" },
  { key: "assets",  title: "Asset Management",          desc: "Lifecycle, warranty and service history for all field equipment.", icon: Inventory2Outlined,              status: "live", to: "/demo/assets",           gradient: "linear-gradient(135deg, #f472b6, #db2777)" },
  { key: "alerts",  title: "Alerts Center",             desc: "Unified faults and notifications across every connected system.", icon: NotificationsActiveOutlined,      status: "live", to: "/demo/alerts",           gradient: "linear-gradient(135deg, #fb7185, #ef4444)" },
];

// ── ServoSaathi capabilities ─────────────────────────────────────────────────
const CAPABILITIES = [
  {
    icon: ManageSearchOutlined,
    gradient: "linear-gradient(135deg, #3b82f6, #22d3ee)",
    glow: "rgba(59,130,246,0.30)",
    title: "Device Intelligence",
    desc: "Get a complete picture of any device or your entire fleet in seconds — performance, history, telemetry and health.",
    prompts: [
      "How is the pump at Ramesh Kumar's farm performing today?",
      "Show me the status of all solar inverters in Rajasthan",
      "Which pumps have been running for more than 8 hours today?",
    ],
  },
  {
    icon: SettingsRemoteOutlined,
    gradient: "linear-gradient(135deg, #34d399, #22d3ee)",
    glow: "rgba(52,211,153,0.28)",
    title: "Natural Language Control",
    desc: "Issue commands in plain language — ServoSaathi authenticates, executes and confirms the action in real time.",
    prompts: [
      "Switch on the pump for customer Surendra Singh",
      "Turn off all pumps in the Jaipur cluster",
      "Start charging session on charger CHG-DL-042",
    ],
  },
  {
    icon: FilterAltOutlined,
    gradient: "linear-gradient(135deg, #fbbf24, #f59e0b)",
    glow: "rgba(251,191,36,0.25)",
    title: "Intelligent Filtering",
    desc: "Slice your data across any dimension — geography, status, customer, fault type or time window — instantly.",
    prompts: [
      "Show me all pumps that went offline in the last 24 hours",
      "Which EV chargers are deployed in Tamil Nadu?",
      "List all devices with active faults across every system",
    ],
  },
  {
    icon: BuildOutlined,
    gradient: "linear-gradient(135deg, #f472b6, #db2777)",
    glow: "rgba(244,114,182,0.25)",
    title: "Field Support & Troubleshooting",
    desc: "Diagnose faults, guide field engineers step by step and track end-to-end service delivery in real time.",
    prompts: [
      "Pump SP-04618 is throwing fault code E24 — what should I do?",
      "Has the engineer resolved Meena Patel's complaint yet?",
      "Walk me through replacing the inverter on plant GJ-08",
    ],
  },
];

// ── Typewriter prompt cycling ────────────────────────────────────────────────
const ALL_PROMPTS = [
  "Switch on Surendra's pump in Hindi...",
  "பம்ப் நிலையைக் காட்டு...",
  "Show all offline devices in Punjab",
  "सभी खराब उपकरणों की सूची दो",
  "Which chargers are free in Chennai?",
  "నా పంప్ ఎందుకు ఆగిపోయింది?",
  "Rajasthan ke pumps ki report do",
];

// ── System card ───────────────────────────────────────────────────────────────
const SystemCard = ({ system, onOpen }) => {
  const Icon = system.icon;
  return (
    <Box
      onClick={onOpen}
      sx={{
        ...glassCard,
        p: 2.6,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          borderColor: demo.borderStrong,
          boxShadow: "0 16px 40px rgba(0,0,0,0.3)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ width: 52, height: 52, borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", background: system.gradient, boxShadow: "0 8px 22px rgba(0,0,0,0.35)" }}>
          <Icon sx={{ color: "#fff", fontSize: 26 }} />
        </Box>
        <Chip label="Live" size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, color: "#062014", backgroundColor: demo.green, "& .MuiChip-label": { px: 1 } }} />
      </Box>
      <Typography sx={{ color: demo.text, fontWeight: 700, fontSize: 17, mb: 0.8 }}>{system.title}</Typography>
      <Typography sx={{ color: demo.textDim, fontSize: 13.5, lineHeight: 1.55, flex: 1 }}>{system.desc}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 2 }}>
        <Typography sx={{ color: demo.accent, fontWeight: 600, fontSize: 13.5 }}>Open system</Typography>
        <ArrowForwardRounded sx={{ color: demo.accent, fontSize: 17 }} />
      </Box>
    </Box>
  );
};

// ── Capability card ───────────────────────────────────────────────────────────
const CapabilityCard = ({ cap, idx }) => {
  const Icon = cap.icon;
  return (
    <Box
      sx={{
        ...glassCard,
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        position: "relative",
        overflow: "hidden",
        animation: `capFadeUp 0.5s ${0.08 * idx}s ease both`,
        "@keyframes capFadeUp": {
          from: { opacity: 0, transform: "translateY(20px)" },
          to:   { opacity: 1, transform: "translateY(0)" },
        },
        // Glow corner
        "&::before": {
          content: '""',
          position: "absolute",
          top: -40, right: -40,
          width: 130, height: 130,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${cap.glow} 0%, transparent 65%)`,
          pointerEvents: "none",
        },
      }}
    >
      {/* Icon + title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.6 }}>
        <Box sx={{ width: 46, height: 46, borderRadius: "13px", display: "flex", alignItems: "center", justifyContent: "center", background: cap.gradient, boxShadow: `0 6px 20px ${cap.glow}`, flexShrink: 0 }}>
          <Icon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Typography sx={{ color: demo.text, fontWeight: 800, fontSize: 16.5, lineHeight: 1.2 }}>{cap.title}</Typography>
      </Box>

      {/* Description */}
      <Typography sx={{ color: demo.textDim, fontSize: 13.5, lineHeight: 1.6 }}>{cap.desc}</Typography>

      {/* Example prompts as chat bubbles */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ color: demo.textFaint, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", mb: 0.3 }}>
          Example prompts
        </Typography>
        {cap.prompts.map((p, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              px: 1.4,
              py: 1,
              borderRadius: "10px",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
              transition: "border-color 0.2s, background 0.2s",
              cursor: "default",
              "&:hover": { background: "rgba(255,255,255,0.07)", borderColor: "rgba(255,255,255,0.14)" },
            }}
          >
            <Box sx={{ mt: 0.25, width: 6, height: 6, borderRadius: "50%", background: cap.gradient.includes("#3b82f6") ? "#22d3ee" : cap.gradient.includes("#34d399") ? "#34d399" : cap.gradient.includes("#fbbf24") ? "#fbbf24" : "#f472b6", flexShrink: 0, boxShadow: `0 0 6px currentColor` }} />
            <Typography sx={{ color: demo.text, fontSize: 13, lineHeight: 1.45 }}>{p}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const DemoLanding = () => {
  const navigate = useNavigate();
  const { clearTokens } = useAuth();
  const [saathiOpen, setSaathiOpen] = useState(false);
  const [promptIdx, setPromptIdx]   = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [typing, setTyping]           = useState(true);

  // Typewriter for hero prompt bar
  useEffect(() => {
    const full = ALL_PROMPTS[promptIdx];
    if (typing) {
      if (displayText.length < full.length) {
        const t = setTimeout(() => setDisplayText(full.slice(0, displayText.length + 1)), 42);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => setTyping(false), 1800);
        return () => clearTimeout(t);
      }
    } else {
      if (displayText.length > 0) {
        const t = setTimeout(() => setDisplayText(displayText.slice(0, -1)), 18);
        return () => clearTimeout(t);
      } else {
        setPromptIdx((i) => (i + 1) % ALL_PROMPTS.length);
        setTyping(true);
      }
    }
  }, [displayText, typing, promptIdx]);

  return (
    <Box sx={{ minHeight: "100vh", width: "100%", background: demo.bgGradient, position: "relative", overflow: "hidden" }}>

      {/* ── Ambient background orbs ── */}
      <Box sx={{ position: "fixed", top: "10%",  left: "-8%",  width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 65%)", pointerEvents: "none", zIndex: 0 }} />
      <Box sx={{ position: "fixed", top: "40%",  right: "-6%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(34,211,238,0.06) 0%, transparent 65%)", pointerEvents: "none", zIndex: 0 }} />
      <Box sx={{ position: "fixed", bottom: "5%",left: "30%",  width: 600, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(167,139,250,0.05) 0%, transparent 65%)", pointerEvents: "none", zIndex: 0 }} />

      {/* ════════════════════════════════════
          TOP BAR
      ════════════════════════════════════ */}
      <Box sx={{ display: "flex", alignItems: "center", px: { xs: 2.5, md: 5 }, py: 1.8, borderBottom: `1px solid ${demo.border}`, position: "sticky", top: 0, zIndex: 100, backdropFilter: "blur(14px)", background: "rgba(7,11,20,0.60)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.3, flex: 1 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "11px", display: "flex", alignItems: "center", justifyContent: "center", background: `linear-gradient(135deg, ${demo.primary}, ${demo.accent})` }}>
            <BoltRounded sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ color: demo.text, fontWeight: 800, fontSize: 17, lineHeight: 1 }}>Servotech One</Typography>
            <Typography sx={{ color: demo.textDim, fontSize: 11.5 }}>Unified Control Center</Typography>
          </Box>
        </Box>

        {/* ServoSaathi badge in topbar */}
        <Box
          onClick={() => setSaathiOpen((s) => !s)}
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1,
            px: 1.6,
            py: 0.6,
            mr: 2,
            borderRadius: "50px",
            background: "rgba(167,139,250,0.10)",
            border: "1px solid rgba(167,139,250,0.30)",
            cursor: "pointer",
            transition: "all 0.2s",
            "&:hover": { background: "rgba(167,139,250,0.18)", borderColor: "rgba(167,139,250,0.50)" },
          }}
        >
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#a78bfa", animation: "aiPulse 2s infinite", "@keyframes aiPulse": { "0%,100%": { boxShadow: "0 0 0 0 rgba(167,139,250,0.7)" }, "50%": { boxShadow: "0 0 0 5px rgba(167,139,250,0)" } } }} />
          <Typography sx={{ color: "#a78bfa", fontSize: 12.5, fontWeight: 700 }}>ServoSaathi AI</Typography>
        </Box>

        <Button onClick={() => { clearTokens(); navigate("/demo/login"); }} startIcon={<LogoutRounded />}
          sx={{ color: demo.textDim, textTransform: "none", fontSize: 14, borderRadius: "10px", border: `1px solid ${demo.border}`, px: 2, "&:hover": { color: demo.text, borderColor: demo.borderStrong, background: demo.surface } }}>
          Sign out
        </Button>
      </Box>

      <Box sx={{ position: "relative", zIndex: 1 }}>

        {/* ════════════════════════════════════
            HERO
        ════════════════════════════════════ */}
        <Box sx={{ px: { xs: 2.5, md: 5 }, pt: { xs: 5, md: 7 }, pb: { xs: 3, md: 4 }, maxWidth: 1200, mx: "auto" }}>

          {/* Badge row */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mb: 3 }}>
            <Chip label="🇮🇳  India's First Agentic AI Energy Dashboard" size="small"
              sx={{ color: "#a78bfa", background: "rgba(167,139,250,0.10)", border: "1px solid rgba(167,139,250,0.28)", fontWeight: 700, fontSize: 12 }} />
            <Chip label="● All systems operational" size="small"
              sx={{ color: demo.green, background: "rgba(52,211,153,0.10)", border: `1px solid rgba(52,211,153,0.30)`, fontWeight: 600, fontSize: 12 }} />
          </Box>

          {/* Headline */}
          <Typography sx={{ color: demo.text, fontWeight: 900, fontSize: { xs: 28, md: 48 }, lineHeight: 1.08, letterSpacing: "-0.8px", mb: 2, maxWidth: 820 }}>
            Monitor. Command. Converse.<br />
            <Box component="span" sx={{ background: `linear-gradient(135deg, ${demo.primary}, ${demo.accent})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Your entire energy fleet
            </Box>
            {" — one intelligent platform."}
          </Typography>

          {/* Sub-headline with the India tagline */}
          <Typography sx={{ color: demo.textDim, fontSize: { xs: 14.5, md: 17 }, maxWidth: 680, lineHeight: 1.7, mb: 4 }}>
            India's first Agentic AI-powered central dashboard to monitor, control and analyse
            every renewable energy device — in your local language. From solar pumps to EV chargers,
            ServoSaathi understands your commands, executes actions and delivers real-time insights
            across your entire fleet.
          </Typography>

          {/* Stats row */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 2, md: 4 }, mb: 5 }}>
            {[
              { value: "50,000+",  label: "Devices Connected" },
              { value: "14",       label: "Indian Languages" },
              { value: "<2s",      label: "AI Response Time" },
              { value: "6",        label: "Energy Systems" },
            ].map((s, i) => (
              <Box key={i} sx={{ textAlign: "left" }}>
                <Typography sx={{ color: demo.text, fontWeight: 900, fontSize: { xs: 22, md: 28 }, lineHeight: 1, background: `linear-gradient(135deg, ${demo.primary}, ${demo.accent})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  {s.value}
                </Typography>
                <Typography sx={{ color: demo.textFaint, fontSize: 12.5, fontWeight: 600, mt: 0.3 }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* Typewriter prompt bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2.2,
              py: 1.4,
              borderRadius: "14px",
              background: "rgba(167,139,250,0.06)",
              border: "1px solid rgba(167,139,250,0.22)",
              maxWidth: 600,
              mb: 1,
              backdropFilter: "blur(8px)",
            }}
          >
            <SmartToyOutlined sx={{ color: "#a78bfa", fontSize: 20, flexShrink: 0 }} />
            <Typography sx={{ color: demo.text, fontSize: 14.5, fontWeight: 500, flex: 1, minHeight: 22 }}>
              {displayText}
              <Box component="span" sx={{ display: "inline-block", width: 2, height: "1em", background: "#a78bfa", ml: 0.3, verticalAlign: "middle", animation: "caretBlink 1s step-end infinite", "@keyframes caretBlink": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0 } } }} />
            </Typography>
            <Box sx={{ px: 1.2, py: 0.4, borderRadius: "7px", background: "rgba(167,139,250,0.15)", border: "1px solid rgba(167,139,250,0.30)" }}>
              <Typography sx={{ color: "#a78bfa", fontSize: 11, fontWeight: 700 }}>ServoSaathi</Typography>
            </Box>
          </Box>
          <Typography sx={{ color: demo.textFaint, fontSize: 12, ml: 0.5 }}>Ask anything about your devices, in any language</Typography>
        </Box>

        {/* ════════════════════════════════════
            SERVOSAATHI SECTION
        ════════════════════════════════════ */}
        <Box sx={{ px: { xs: 2.5, md: 5 }, mb: 5, maxWidth: 1200, mx: "auto" }}>

          {/* Section card */}
          <Box
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(167,139,250,0.25)",
              overflow: "hidden",
              background: "rgba(167,139,250,0.04)",
              backdropFilter: "blur(12px)",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0, left: 0, right: 0,
                height: "1px",
                background: "linear-gradient(90deg, transparent, rgba(167,139,250,0.7), rgba(34,211,238,0.5), transparent)",
              },
            }}
          >
            {/* Header row — always visible, click to expand */}
            <Box
              onClick={() => setSaathiOpen((s) => !s)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2.5,
                px: { xs: 2.5, md: 4 },
                py: 3,
                cursor: "pointer",
                userSelect: "none",
                "&:hover .expand-hint": { color: demo.text },
              }}
            >
              {/* AI Orb */}
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #7c3aed, #a78bfa, #22d3ee)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 0 0 0 rgba(167,139,250,0.6)",
                    animation: "orbPulse 3s ease-in-out infinite",
                    "@keyframes orbPulse": {
                      "0%":   { boxShadow: "0 0 0 0 rgba(167,139,250,0.55)" },
                      "60%":  { boxShadow: "0 0 0 14px rgba(167,139,250,0)" },
                      "100%": { boxShadow: "0 0 0 0 rgba(167,139,250,0)" },
                    },
                  }}
                >
                  <SmartToyOutlined sx={{ color: "#fff", fontSize: 32 }} />
                </Box>
                {/* Orbiting ring */}
                <Box sx={{
                  position: "absolute", top: "50%", left: "50%",
                  width: 88, height: 88,
                  marginTop: "-44px", marginLeft: "-44px",
                  borderRadius: "50%",
                  border: "1.5px dashed rgba(167,139,250,0.35)",
                  animation: "orbRing 8s linear infinite",
                  "@keyframes orbRing": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
                  pointerEvents: "none",
                }} />
                {/* Dot on ring */}
                <Box sx={{
                  position: "absolute", top: "50%", left: "50%",
                  width: 8, height: 8,
                  marginTop: "-46px", marginLeft: "-4px",
                  borderRadius: "50%",
                  background: "#a78bfa",
                  boxShadow: "0 0 10px #a78bfa",
                  animation: "orbRing 8s linear infinite",
                  pointerEvents: "none",
                }} />
              </Box>

              {/* Text */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 0.5 }}>
                  <Typography sx={{ color: demo.text, fontWeight: 900, fontSize: { xs: 19, md: 24 } }}>
                    Meet{" "}
                    <Box component="span" sx={{ background: "linear-gradient(135deg, #a78bfa, #22d3ee)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                      ServoSaathi
                    </Box>
                  </Typography>
                  <Chip label="Agentic AI" size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, color: "#a78bfa", background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.30)", "& .MuiChip-label": { px: 0.8 } }} />
                </Box>
                <Typography sx={{ color: demo.textDim, fontSize: { xs: 13, md: 14.5 }, lineHeight: 1.5 }}>
                  The world's first renewable energy AI agent that understands, speaks and acts —
                  in Hindi, Tamil, Telugu, Marathi, Gujarati, Bengali and more.
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 1.2 }}>
                  {["Hindi", "Tamil", "Telugu", "Marathi", "Gujarati", "Bengali", "Kannada", "Punjabi", "+6 more"].map((lang) => (
                    <Box key={lang} sx={{ px: 1, py: 0.3, borderRadius: "5px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}>
                      <Typography sx={{ color: demo.textDim, fontSize: 11, fontWeight: 600 }}>{lang}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Expand toggle */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                <Box
                  sx={{
                    px: 2.2,
                    py: 0.9,
                    borderRadius: "50px",
                    background: saathiOpen ? "rgba(167,139,250,0.15)" : "linear-gradient(135deg, #7c3aed, #a78bfa)",
                    border: "1px solid rgba(167,139,250,0.4)",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    transition: "all 0.25s",
                  }}
                >
                  <AutoAwesomeOutlined sx={{ color: "#fff", fontSize: 16 }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" }}>
                    {saathiOpen ? "Hide features" : "See what it can do"}
                  </Typography>
                  {saathiOpen
                    ? <KeyboardArrowUpRounded sx={{ color: "#fff", fontSize: 18 }} />
                    : <KeyboardArrowDownRounded sx={{ color: "#fff", fontSize: 18 }} />}
                </Box>
              </Box>
            </Box>

            {/* ── Expandable capabilities grid ── */}
            {saathiOpen && (
              <Box sx={{ px: { xs: 2.5, md: 4 }, pb: 4 }}>
                {/* Divider */}
                <Box sx={{ height: 1, background: "linear-gradient(90deg, transparent, rgba(167,139,250,0.3), rgba(34,211,238,0.2), transparent)", mb: 3.5 }} />

                {/* Section label */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 3 }}>
                  <TranslateOutlined sx={{ color: "#a78bfa", fontSize: 18 }} />
                  <Typography sx={{ color: demo.textDim, fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    4 Core Capabilities
                  </Typography>
                </Box>

                {/* 2×2 capability grid */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5, mb: 4 }}>
                  {CAPABILITIES.map((cap, i) => (
                    <CapabilityCard key={i} cap={cap} idx={i} />
                  ))}
                </Box>

                {/* Bottom CTA */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                    p: 2.8,
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, rgba(124,58,237,0.15), rgba(34,211,238,0.08))",
                    border: "1px solid rgba(167,139,250,0.22)",
                  }}
                >
                  <Box>
                    <Typography sx={{ color: demo.text, fontWeight: 700, fontSize: 16.5 }}>
                      Ready to experience Agentic AI?
                    </Typography>
                    <Typography sx={{ color: demo.textDim, fontSize: 13.5, mt: 0.4 }}>
                      Click the ServoSaathi button in the corner to start a conversation.
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2.4,
                        py: 1.2,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
                        boxShadow: "0 8px 24px rgba(124,58,237,0.35)",
                        cursor: "default",
                        animation: "ctaPulse 3s ease-in-out infinite",
                        "@keyframes ctaPulse": {
                          "0%,100%": { boxShadow: "0 8px 24px rgba(124,58,237,0.35)" },
                          "50%":     { boxShadow: "0 8px 36px rgba(124,58,237,0.55)" },
                        },
                      }}
                    >
                      <SmartToyOutlined sx={{ color: "#fff", fontSize: 20 }} />
                      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>ServoSaathi</Typography>
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: demo.green, ml: 0.5, boxShadow: `0 0 8px ${demo.green}` }} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* ════════════════════════════════════
            SYSTEMS GRID
        ════════════════════════════════════ */}
        <Box sx={{ px: { xs: 2.5, md: 5 }, pb: 10, maxWidth: 1200, mx: "auto" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Typography sx={{ color: demo.textDim, fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Connected Systems
            </Typography>
            <Box sx={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${demo.border}, transparent)` }} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" }, gap: 2.5 }}>
            {SYSTEMS.map((s) => (
              <SystemCard key={s.key} system={s} onOpen={() => navigate(s.to)} />
            ))}
          </Box>
        </Box>

      </Box>{/* /zIndex wrapper */}

      {/* Floating voice-assistant launcher */}
      <Box
        onClick={() => navigate("/demo/voice")}
        sx={{
          position: "fixed", bottom: 30, left: 28, zIndex: 1300,
          display: "flex", alignItems: "center", gap: 1, cursor: "pointer",
          px: 2, py: 1.2, borderRadius: "999px",
          background: `linear-gradient(135deg, ${demo.primary}, ${demo.accent})`,
          boxShadow: "0 10px 30px rgba(34,211,238,0.35)",
          transition: "transform 0.2s ease",
          "&:hover": { transform: "translateY(-2px)" },
        }}
      >
        <KeyboardVoiceRounded sx={{ color: "#fff" }} />
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
          Talk to ServoSaathi
        </Typography>
      </Box>

      <ChatWidget />
    </Box>
  );
};

export default DemoLanding;
